<script setup lang="ts">
import { doc } from "firebase/firestore";

const props = defineProps({
  documentId: {
    type: String,
    required: true,
  },
  criteriaInstance: {
    type: Object as PropType<CriteriaInstance>,
    required: true,
  },
  checklistCriteria: {
    type: Object as PropType<ChecklistCriteria>,
    required: true,
  },
});

const submittedDocumentSTore = useSubmittedDocumentStore(props.documentId);
const { submittedDocument, selectedTestMetric } = storeToRefs(
  submittedDocumentSTore
);

const documentSelectionStore = useDocumentSelectionStore(props.documentId);
const { selectedParagraphs } = storeToRefs(documentSelectionStore);

const documentParagraphsStore = useDocumentParagraphsStore(props.documentId);
const { paragraphs } = storeToRefs(documentParagraphsStore);

const checklistStore = useDocumentChecklistStore(props.documentId);
const { useChecklist } = storeToRefs(checklistStore);

const isSaving = ref(false);
const saveTestDocument = async () => {
  isSaving.value = true;
  if (selectedTestMetric.value == undefined) {
    useBaseToast("Please select a metric", "error");
    return;
  }

  let paragraphsToUse =
    selectedParagraphs.value.length > 0
      ? selectedParagraphs.value
      : paragraphs.value;

  paragraphsToUse = paragraphsToUse
    .map((paragraph) => {
      const documentParagraphStore = useDocumentParagraphStore(
        props.documentId,
        paragraph.id!
      );

      return documentParagraphStore.paragraph;
    })
    .filter((paragraph) => paragraph != undefined);

  let label = submittedDocument.value?.name ?? "";

  if (submittedDocument.value?.studentName != undefined) {
    label += " - " + submittedDocument.value?.studentName;
  }

  const response = await useCreateEvaluationTestWithParagraphs({
    paragraphs: paragraphsToUse,
    metricId: selectedTestMetric.value ?? "",
    checklistCriteriaId: props.checklistCriteria.id!,
    checklistId: useChecklist.value?.id ?? "",
    checklistName: useChecklist.value?.title ?? "",
    label: label,
  });

  isSaving.value = false;

  useBaseToast("Test document created successfully", "success");
};

const metrics = ref<EvaluationMetric[]>([]);
const { evaluationMetricsStream } = useEvaluationMetrics();

const { data } = evaluationMetricsStream;
</script>

<template>
  <div class="flex flex-col items-center p-2 w-[300px]">
    <BaseSelectInput
      placeholder="Select Metric"
      v-model="selectedTestMetric"
      :options="data"
      class="mb-1"
      label="name"
      value-prop="id"
    />
    <BaseTextButton
      color="primary"
      @click.stop.prevent="saveTestDocument"
      :show-spinner="isSaving"
    >
      <div class="flex flex-row items-center gap-1">
        <BaseIconSave />
        <span>Save Test Document</span>
      </div>
    </BaseTextButton>
  </div>
</template>

<style scoped></style>
